import moment from 'moment'
import { errors } from 'utils/error-handling/errors'
import {
  type EntityIdInput,
  OverrideType,
  type ValueBasedOverrideRequestInput,
} from 'domains/products/api'
import {
  type Attribute,
  type AttributeOverrideState,
  sourceTypeAllSources,
} from 'domains/products/components/view/types/attributeOverride.types'

const remoteUserAppName = 'RPP'

export const mapFormDataToValueBasedOverrideInput = (
  formData: AttributeOverrideState,
  attributes: { [key: string]: Attribute },
  productId: string,
  retailerId: string,
  currentAccount
): ValueBasedOverrideRequestInput => {
  const vboInput: ValueBasedOverrideRequestInput = {
    entityId: getEntityId(productId, retailerId),
    attributeName: attributes[formData.attribute]?.name,
    overrideType: formData.overrideType,
    sourceValue: getValue(formData.value),
    overrideValue: getOverrideValue(formData.overrideType, formData.newValue),
    createdBy: getUserInfo(currentAccount),
    createdWhy: formData.comment,
    sourceType: getSourceType(formData.sourceType),
    expiryDate: getExpiryDate(formData.expiryDate),
    ticketUrl: formData.ticketEntry || undefined,
  }

  return vboInput
}

export const getEntityId = (productId: string, retailerId: string): EntityIdInput => {
  if (retailerId) {
    return {
      retailerProductId: {
        retailerId: retailerId,
        productId: productId,
      },
    }
  }

  return {
    productId: {
      productId: productId,
      locale: 'EN_US',
    },
  }
}

const getSourceType = (sourceType: string): string => {
  if (sourceType === sourceTypeAllSources) {
    return undefined
  }
  return sourceType
}

const getValue = (value: unknown): string => {
  const val = {
    value: value,
  }

  return JSON.stringify(val)
}

const getOverrideValue = (overrideType: OverrideType, newValue): string => {
  if (overrideType === OverrideType.OverrideTypeExclude) {
    newValue = ''
  }

  return getValue(newValue)
}

const getUserInfo = currentAccount => ({
  appName: remoteUserAppName,
  userId: currentAccount.id,
  userName: currentAccount.email,
})

const getExpiryDate = (expiryDate: string): string => {
  if (!expiryDate) {
    return undefined
  }
  const newExpiryDate = moment(expiryDate, 'YYYY-MM-DD', true).hour(23).minute(59).second(59)
  if (!newExpiryDate.isValid()) return undefined
  return newExpiryDate.utc().format()
}

export const getDisplayValue = (value: string) => {
  try {
    const valueJson: { value: string | boolean | number | undefined } | undefined =
      JSON.parse(value)

    if (!valueJson) {
      return ''
    }

    const finalValue = valueJson.value

    if (finalValue === undefined) {
      return ''
    }

    if (typeof finalValue === 'string') {
      return finalValue
    }

    return JSON.stringify(finalValue)
  } catch (err) {
    errors.captureWarning(err)
    return ''
  }
}

export const getDisplayRemoteUser = user => {
  if (!user) {
    return ''
  }
  try {
    const remoteUser = JSON.parse(user)
    return remoteUser.user_name || ''
  } catch (err) {
    errors.captureWarning(err)
    return ''
  }
}

export const getDisplayExpiryDate = (expiryDate): string => {
  if (!expiryDate) {
    return ''
  }
  const newExpiryDate = moment(expiryDate)
  return newExpiryDate.local().format('MM/DD/YYYY')
}
